import { needsMowing, Tile } from '../../sim/grid';
import { Rng } from '../../sim/rng';
import type { SimLevel } from '../../sim/level';
import type { MowerSpec } from '../../sim/mower';
import { Simulation } from '../../sim/simulation';
import { ACTIONS, motionModelFor, type Action } from './motionModel';
import { StateSpace } from './stateSpace';
import type { SimRules } from '../../sim/simRules';

const ACTION_CODE: Record<Action, number> = { straight: 0, left: 1, right: 2, reverse: 3 };

/** Tiles kept off while there is another way: they cost coins, or a flat tire. */
const AVOID: readonly Tile[] = [
  Tile.FlowerBed,
  Tile.Ornament,
  Tile.Hose,
  Tile.Sprinkler,
  Tile.Thorns,
];
/** Smashing anyway (the gold time's routes): only thorns, whose flat tire costs time. */
const AVOID_SMASHING: readonly Tile[] = [Tile.Thorns];

/**
 * How the planner picks its next stretch. The game's autopilot uses the defaults; the par and
 * gold-time searches (`bestClear`) try several.
 */
export interface PlannerOptions {
  /** Drive through whatever the mower can break, and over flowers and hoses (the gold time). */
  smash?: boolean;
  /**
   * Look this many steps past the nearest grass and take the stretch that cuts the most for its
   * length. 0 (the default): the nearest grass.
   */
  window?: number;
  /** Seeds small random nudges between nearly equal stretches, so each try drives differently. */
  seed?: string;
  /**
   * Sweep in lanes, as a player mows a lawn: stretches driven along `axis` ('x': east-west) on
   * every `width`-th row or column (from `offset`) count `bonus` times as much. Leftovers off the
   * lanes are still mowed. Needs a window.
   */
  lanes?: { axis: 'x' | 'y'; width: number; offset: number; bonus: number };
}

/**
 * Greedy coverage autopilot: at every tile center, drive toward the nearest unmowed grass using
 * only safe states (never into a trap). Not optimal, but a win proves the level is beatable with
 * that mower. Static obstacles only; critters arrive in Phase 2.
 */
export class GreedyPlanner {
  private readonly space: StateSpace;
  private readonly safe: Uint8Array;
  private readonly parent: Int32Array;
  private readonly firstAction: Int8Array;
  private readonly depth: Int32Array;
  private readonly rng: Rng | null;

  constructor(
    private readonly sim: Simulation,
    private readonly options: PlannerOptions = {},
  ) {
    this.space = new StateSpace(sim.grid, motionModelFor(sim.mowerSpec), {
      cutPower: sim.mowerSpec.cutPower,
      cutWidth: sim.cutWidth,
      side: sim.mower.side,
      wings: !!sim.mowerSpec.wings,
      ...(options.smash ? { breakPower: sim.mowerSpec.breakPower } : {}),
    });
    this.safe = this.space.computeSafe();
    this.parent = new Int32Array(this.space.size);
    this.firstAction = new Int8Array(this.space.size);
    this.depth = new Int32Array(this.space.size);
    this.rng = options.seed ? Rng.fromSeed(options.seed) : null;
  }

  /** True if the mower's current state can avoid crashing forever. */
  isCurrentStateSafe(): boolean {
    return this.safe[this.currentState()] === 1;
  }

  /**
   * The action to take at the current tile center, or null when no unmowed grass is reachable
   * through safe states. Only meaningful while the mower is centered (progress 0).
   */
  nextAction(stuck = false): Action | null {
    // Going round in circles (a squirrel or rabbit keeps the last grass from it): drive through
    // the critter, which only poofs away (rules 6), no crash.
    if (stuck && this.sim.rules.softSmallCritters) {
      const through = this.search(true, true, true) ?? this.search(false, true, true);
      if (through) return through;
    }
    // Prefer not to trample flower beds; drive over them only when nothing else is reachable.
    // If the only grass left is under critters, head for it anyway (never into them): they flee.
    return this.search(true, true) ?? this.search(false, true) ?? this.search(false, false);
  }

  /** Whether a squirrel or rabbit is at (x, y): bumping one only poofs it (rules 6). */
  private softCritterAt(x: number, y: number): boolean {
    const kind = this.sim.critters.list[this.sim.critters.indexAt(x, y)]?.kind;
    return kind === 'squirrel' || kind === 'rabbit';
  }

  private search(
    avoidFlowers: boolean,
    routeAroundCritters: boolean,
    throughSoft = false,
  ): Action | null {
    const start = this.currentState();
    const grid = this.sim.grid;
    const avoid = this.options.smash ? AVOID_SMASHING : AVOID;
    const window = this.options.window ?? 0;
    // With a window: the best stretch found within `window` steps past the nearest grass.
    let best = -1;
    let bestScore = -Infinity;
    let limit = Infinity;
    this.parent.fill(-1);
    this.parent[start] = start;
    this.depth[start] = 0;
    const queue = [start];
    const next: number[] = [];
    const via: Action[] = [];
    for (let head = 0; head < queue.length; head++) {
      const i = queue[head] as number;
      if ((this.depth[i] as number) >= limit) break;
      next.length = 0;
      via.length = 0;
      this.space.forEachSuccessor(i, (j, action) => {
        next.push(j);
        via.push(action);
      });
      for (let k = 0; k < next.length; k++) {
        const j = next[k] as number;
        if (this.parent[j] !== -1 || !this.safe[j]) continue;
        const { x, y, dir } = this.space.decode(j);
        const covered = this.space.footprint(x, y, dir);
        // Flowers, decorations and hoses cost coins, thorns a flat tire: keep off them while there
        // is another way.
        if (avoidFlowers && covered.some((p) => avoid.includes(grid.tileAt(p.x, p.y)))) continue;
        // Critters block like obstacles right now; plans are redone at every center as they move.
        // The first step must always avoid them; later steps may pass through if they'll flee.
        const critter = covered.some(
          (p) =>
            (this.sim.critters.occupied(p.x, p.y) &&
              !(throughSoft && this.softCritterAt(p.x, p.y))) ||
            this.sim.rivalOccupies(p.x, p.y),
        );
        if (critter && (routeAroundCritters || i === start)) continue;
        // A stray ball coming down a lane: never step into its way.
        if (
          i === start &&
          covered.some((p) => this.sim.ballDanger(p.x, p.y, 0, 2 * this.sim.crossTicks()))
        )
          continue;
        // The rival robot can sit in a pocket's only way out: never step into one it has closed.
        if (i === start && this.sim.rival && !this.hasWayOut(j)) continue;
        this.parent[j] = i;
        this.depth[j] = (this.depth[i] as number) + 1;
        this.firstAction[j] =
          i === start ? ACTION_CODE[via[k] as Action] : (this.firstAction[i] as number);
        const gain = this.space
          .mowedAt(x, y, dir)
          .filter((p) => needsMowing(grid.tileAt(p.x, p.y))).length;
        if (gain > 0) {
          if (window === 0) return ACTIONS[this.firstAction[j]] ?? null;
          const d = this.depth[j];
          if (limit === Infinity) limit = d + window;
          const nudge = this.rng ? 1 + 0.3 * this.rng.nextFloat() : 1;
          const lanes = this.options.lanes;
          const along =
            lanes &&
            (lanes.axis === 'x' ? dir === 1 || dir === 3 : dir === 0 || dir === 2) &&
            ((lanes.axis === 'x' ? y : x) - lanes.offset) % lanes.width === 0;
          const score = (gain / (d + 1)) * nudge * (along ? lanes.bonus : 1);
          if (score > bestScore) {
            bestScore = score;
            best = j;
          }
        }
        queue.push(j);
      }
    }
    return best >= 0 ? (ACTIONS[this.firstAction[best] as number] ?? null) : null;
  }

  /** Whether a state has a safe next step that the rival robot is not on right now. */
  private hasWayOut(i: number): boolean {
    let out = false;
    this.space.forEachSuccessor(i, (k) => {
      if (out || !this.safe[k]) return;
      const { x, y, dir } = this.space.decode(k);
      out = !this.space.footprint(x, y, dir).some((p) => this.sim.rivalOccupies(p.x, p.y));
    });
    return out;
  }

  private currentState(): number {
    const m = this.sim.mower;
    return this.space.index(m.x, m.y, m.dir, this.space.model.fromMower(m));
  }
}

export interface SolveResult {
  won: boolean;
  /** Ticks taken (to win, or until giving up). */
  ticks: number;
  status: Simulation['status'];
  /** Percent of grass mowed at the end. */
  percentMowed: number;
  /**
   * Tile centers reached, in order (starting with the start tile), with the fuel each step burned
   * (more than 1 through tall grass; 1 for mowers without a tank).
   */
  route: { x: number; y: number; fuel: number }[];
  /** Hits taken (a fair level is beatable without any). */
  crashes: number;
  /** Flat tires from thorn patches (a fair lawn never needs to cross one). */
  flats: number;
  sim: Simulation;
}

export interface SolveOptions {
  maxTicks?: number;
  /** Run with an endless tank (to find the route, and its fuel use, before placing gas cans). */
  ignoreFuel?: boolean;
  /** The sim's rules to play by (absent: the game's own, src/sim/simRules.ts). */
  rules?: Readonly<SimRules>;
  /** How the planner picks its stretches (absent: the game's own autopilot). */
  planner?: PlannerOptions;
}

/** Reaching one tile center facing one way more often than this is going round in circles. */
const STUCK_VISITS = 8;

/** Plays a level headlessly with the greedy planner. */
export function solveGreedy(
  level: SimLevel,
  mower: MowerSpec,
  { maxTicks = 200_000, ignoreFuel = false, rules, planner: how = {} }: SolveOptions = {},
): SolveResult {
  // An "endless" tank is just a huge one, so the fuel each step burns can still be measured.
  const spec: MowerSpec =
    ignoreFuel && mower.fuelTiles !== undefined ? { ...mower, fuelTiles: 1_000_000_000 } : mower;
  const sim = new Simulation(level, spec, {}, [], rules);
  sim.drainEvents();
  let planner = new GreedyPlanner(sim, how);
  const route = [{ x: sim.mower.x, y: sim.mower.y, fuel: 0 }];
  let fuel = sim.mower.fuel;
  let crashes = 0;
  let flats = 0;
  // Times each tile center was reached facing each way: past STUCK_VISITS, the mower is going
  // round in circles.
  const visits = new Map<string, number>();
  let arrived = '';
  while (sim.status === 'running' && sim.tick < maxTicks) {
    if (sim.mower.progress === 0) {
      const m = sim.mower;
      const state = `${m.x},${m.y},${m.dir}`;
      let seen = visits.get(state) ?? 0;
      if (state !== arrived) visits.set(state, ++seen);
      arrived = state;
      const action = planner.nextAction(seen > STUCK_VISITS);
      if (action === null) break;
      if (action !== 'straight') sim.enqueue({ type: 'turn', turn: action });
    }
    for (const e of sim.step()) {
      // (A stray ball's hit counts too: a fair lawn is beatable without one.)
      if (e.type === 'crashed' || e.type === 'ballHit') crashes++;
      if (e.type === 'flatTire' && !e.saved) flats++;
      // A fallen tree changes the lawn: work out the safe states again.
      if (e.type === 'treeFell') planner = new GreedyPlanner(sim, how);
    }
    const m = sim.mower;
    const last = route[route.length - 1];
    if (m.progress === 0 && (last?.x !== m.x || last.y !== m.y)) {
      // Burned this step; a gas can refilling on arrival hides it, so count it as one tile.
      const burned = Number.isFinite(m.fuel) && m.fuel < fuel ? fuel - m.fuel : 1;
      route.push({ x: m.x, y: m.y, fuel: burned });
    }
    fuel = m.fuel;
  }
  return {
    won: sim.status === 'won',
    ticks: sim.tick,
    status: sim.status,
    percentMowed: sim.grid.percentMowed,
    route,
    crashes,
    flats,
    sim,
  };
}

/** How many route tries a par or gold-time search makes after the autopilot's own. */
export const ROUTE_TRIES = 24;

/**
 * Route try `i` (0 is the game's own autopilot; the rest weigh grass cut against distance with
 * seeded nudges). The par search uses tries 0 to ROUTE_TRIES; the gold time also tries each of
 * them smashing, numbered after those.
 */
export function routeTry(i: number): PlannerOptions {
  const smash = i > ROUTE_TRIES;
  const t = smash ? i - ROUTE_TRIES - 1 : i;
  if (t === 0) return { smash };
  return { smash, window: 1 + ((t - 1) % 4), seed: `route:${t - 1}` };
}

/**
 * The fastest clean clear (no hits, no flat tire) over route tries `from` to `to`: the par time
 * (0 to ROUTE_TRIES, never smashing) or the gold time (every try, smashing too: owner,
 * 2026-10-31, par never smashes and the gold time may). Null when no try clears it cleanly.
 */
export function bestClear(
  level: SimLevel,
  mower: MowerSpec,
  { smash = false, maxTicks }: { smash?: boolean; maxTicks?: number } = {},
): { ticks: number; try: number } | null {
  let best: { ticks: number; try: number } | null = null;
  const last = smash ? 2 * ROUTE_TRIES + 1 : ROUTE_TRIES;
  for (let i = 0; i <= last; i++) {
    const r = solveGreedy(level, mower, {
      ...(maxTicks ? { maxTicks } : {}),
      planner: routeTry(i),
    });
    if (!r.won || r.crashes > 0 || r.flats > 0) continue;
    if (!best || r.ticks < best.ticks) best = { ticks: r.ticks, try: i };
  }
  return best;
}
